import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import { useAuth } from "../contexts/AuthContext";

// Detect in-app browsers that Google blocks (Messenger, Instagram, Facebook, TikTok, etc.)
function isInAppBrowser() {
  const ua = navigator.userAgent;
  return /FBAN|FBAV|FB_IAB|MessengerForiOS|Instagram|Musical\.ly|TikTok|Twitter|LinkedInApp|Snapchat|Pinterest|Line\/|WhatsApp/i.test(ua)
    || (/Android/.test(ua) && /wv/.test(ua)); // Android WebView
}

export default function Login() {
  const { login } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  const inAppBrowser = isInAppBrowser();
  const appUrl = window.location.href;

  // Detect platform
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  // Capture the Android/Chrome install prompt before it auto-dismisses
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  // Show button when: not already installed AND (Android prompt ready OR is iOS Safari)
  const showInstallBtn = !isStandalone && (deferredPrompt !== null || isIOS);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 360, width: "100%", textAlign: "center" }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          ✈️ TravelSplit
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Split travel expenses with family and friends
        </Typography>

        {inAppBrowser ? (
          <Box>
            <Typography variant="body2" color="error" fontWeight={700} sx={{ mb: 1 }}>
              Sign-in is blocked in this browser
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Google doesn't allow sign-in from apps like Messenger or Instagram.
              Please open this link in <strong>Safari</strong> or <strong>Chrome</strong>.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{ py: 1.5 }}
              onClick={() => {
                // Copy URL to clipboard so user can paste it in Safari/Chrome
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(appUrl).catch(() => {});
                }
                // On iOS, try to open in Safari directly
                window.location.href = appUrl.replace(/^https?/, "googlechrome") ;
              }}
            >
              Open in Chrome
            </Button>
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1.5 }}>
              Or copy this address and paste it in Safari / Chrome:
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, wordBreak: "break-all", color: "text.secondary", fontFamily: "monospace" }}
            >
              {appUrl}
            </Typography>
          </Box>
        ) : (
          <>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={login}
              sx={{ py: 1.5 }}
            >
              Sign in with Google
            </Button>
            {showInstallBtn && (
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={handleInstall}
                sx={{ py: 1.5, mt: 1.5 }}
                startIcon={<InstallMobileIcon />}
              >
                Install App
              </Button>
            )}
          </>
        )}
      </Paper>

      {/* iOS "Add to Home Screen" guide dialog */}
      <Dialog
        open={showIOSGuide}
        onClose={() => setShowIOSGuide(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Install TravelSplit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            To add this app to your iPhone home screen:
          </Typography>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              1. Tap the <strong>Share</strong> button (⎋) in Safari's bottom toolbar
            </Typography>
            <Typography variant="body2">
              2. Scroll down and tap <strong>"Add to Home Screen"</strong>
            </Typography>
            <Typography variant="body2">
              3. Tap <strong>"Add"</strong> in the top-right corner
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mt: 2 }}
          >
            Works in Safari only — not available in Chrome or Firefox on iOS.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setShowIOSGuide(false)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
